<script setup>
import { onBeforeUnmount, ref, computed } from 'vue'

// Atencão! Não remover este import especificamente falando.
import ImoIcon from '@/components/icons/ImoIcon.vue'

const emit = defineEmits(['dismiss'])

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  message: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  variant: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'warning', 'success'].includes(value),
  },
  dismissible: {
    type: Boolean,
    default: true,
  },
  position: {
    type: String,
    default: 'top-right',
    validator: value => ['top-right', 'top-center', 'bottom-right'].includes(value),
  },
  duration: {
    type: Number,
    default: 5000, // Padrão de 5 segundos
  },
})

const isVisible = ref(false)
const durationId = ref(null)

// Expor método para o plugin chamar
const show = () => {
  isVisible.value = true
  startAutoClose()
}

defineExpose({ show })

const setIcon = computed(() => {
  return {
    success: 'tick-circle',
    info: 'info-circle',
    warning: 'info-circle',
    error: 'close-circle',
  }[props.variant]
})

const closeToast = () => {
  isVisible.value = false
  emit('dismiss')
  if (durationId.value) {
    clearTimeout(durationId.value)
  }
}

const startAutoClose = () => {
  if (props.duration && props.dismissible) {
    durationId.value = setTimeout(() => {
      closeToast()
    }, props.duration)
  }
}

onBeforeUnmount(() => {
  if (durationId.value) {
    clearTimeout(durationId.value)
  }
})
</script>

<template>
  <transition name="fade">
    <div
      v-if="isVisible"
      class="fixed z-50 ml-4 w-auto rounded-[10px] text-sm shadow-lg"
      :class="{
        [variant]: variant,
        'sm:w-[694px]': size === 'lg',
        'sm:w-[494px]': size === 'md',
        'sm:w-[354px]': size === 'sm',
      }"
      role="toast"
    >
      <div class="flex grow flex-row items-center gap-2 px-4 py-2">
        <ImoIcon :size="24" :name="setIcon" />
        <div class="flex grow flex-col gap-1">
          <h1 v-if="title" class="font-semibold">{{ title }}</h1>
          <div
            :class="{
              'text-info-70': variant === 'info',
              'text-error-70': variant === 'error',
              'text-warning-90': variant === 'warning',
              'text-success-80': variant === 'success',
            }"
          >
            {{ message }}
          </div>
        </div>
        <ImoIcon
          v-if="dismissible"
          :size="24"
          name="close-square"
          @click="closeToast"
          class="cursor-pointer"
        />
      </div>
    </div>
  </transition>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.info {
  @apply bg-info-0 text-info-90;
}

.error {
  @apply bg-error-20 text-error-60;
}

.warning {
  @apply bg-warning-20 text-warning-100;
}

.success {
  @apply bg-success-20 text-success-100;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
